import layoutTable from "../../data/tables/ui/layout.json";
import type {
  RegionName,
  UiHudPreset,
  UiLayoutTable,
  UiRegionDef,
} from "./types";

const layout = layoutTable as UiLayoutTable;

const REGION_ORDER: RegionName[] = ["top", "mid", "bottom", "modal"];

export type HudShellHandle = {
  root: HTMLElement;
  region: (name: RegionName) => HTMLElement;
  destroy: () => void;
};

/** Reset menu-scene leftovers (paddingTop etc.) so play HUDs stay top-aligned. */
export function resetFrameShellStyles(frame: HTMLElement): void {
  frame.style.padding = "";
  frame.style.paddingTop = "";
  frame.style.paddingBottom = "";
  frame.style.paddingLeft = "";
  frame.style.paddingRight = "";
  frame.style.margin = "";
  frame.style.marginTop = "";
  // Roster (and similar) may set overflowY=auto; restore CSS #ui-safe-frame default.
  frame.style.overflow = "";
  frame.style.overflowX = "";
  frame.style.overflowY = "";
  frame.style.minHeight = "";
}

/**
 * Build region slots inside SafeFrame from layout + hud preset data.
 */
export function mountHudShell(
  frame: HTMLElement,
  preset: UiHudPreset,
): HudShellHandle {
  frame.replaceChildren();
  resetFrameShellStyles(frame);
  frame.style.display = "flex";
  frame.style.flexDirection = preset.shell.direction;
  frame.style.justifyContent = preset.shell.justify;
  frame.style.alignItems = preset.shell.align;
  frame.style.pointerEvents = "none";
  frame.style.boxSizing = "border-box";
  frame.style.position = "relative";
  frame.style.overflow = "hidden";

  const regions = new Map<RegionName, HTMLElement>();

  for (const name of REGION_ORDER) {
    const el = document.createElement("div");
    el.dataset.region = name;
    const layoutDef = layout.regions[name] as UiRegionDef | undefined;
    const hudRegion = preset.regions[name];
    if (hudRegion?.className) el.className = hudRegion.className;

    applyRegionLayout(el, name, layoutDef, hudRegion?.paddingToken);

    if (name === "bottom" && preset.dock) {
      el.style.setProperty("--ui-dock-columns", String(preset.dock.columns));
    }

    frame.appendChild(el);
    regions.set(name, el);
  }

  return {
    root: frame,
    region: (name) => {
      const el = regions.get(name);
      if (!el) throw new Error(`Missing HUD region: ${name}`);
      return el;
    },
    destroy: () => {
      clearHudShell(frame);
    },
  };
}

function applyRegionLayout(
  el: HTMLElement,
  name: RegionName,
  def: UiRegionDef | undefined,
  paddingOverride?: string,
): void {
  if (!def) return;
  if (name === "modal" || def.position === "absolute") {
    el.style.position = "absolute";
    el.style.inset = "0";
    el.style.zIndex = "10";
    return;
  }
  if (def.flex) el.style.flex = def.flex;
  const padToken =
    paddingOverride !== undefined ? paddingOverride : def.paddingToken;
  if (padToken) {
    el.style.padding = `var(--ui-space-${padToken})`;
  } else if (paddingOverride === "") {
    el.style.padding = "0";
  }
}

/** Clear frame children without destroying SafeFrame element. */
export function clearHudShell(frame: HTMLElement): void {
  frame.replaceChildren();
  resetFrameShellStyles(frame);
  frame.style.display = "";
  frame.style.flexDirection = "";
  frame.style.justifyContent = "";
  frame.style.alignItems = "";
  frame.style.pointerEvents = "none";
}
